import { z } from 'zod';
import type { EventType } from './events';
import { PageQuerySchema } from './pagination';
import type { PriorityUrgency } from './projects';
import { TicketKeySchema } from './tickets';

/*
 * Notifications (Unit 08; S-08-01 … S-08-05). Channel × event type × priority band
 * preferences with defaults when no row exists; in-app inbox rows carry a rendered title /
 * body so the portal and admin inboxes never resolve labels themselves.
 */

export const NOTIFICATION_CHANNELS = ['in_app', 'email'] as const;
export const NotificationChannelSchema = z.enum(NOTIFICATION_CHANNELS);
export type NotificationChannel = z.infer<typeof NotificationChannelSchema>;

/** `high` when `priority_level.urgency = 'high'`, else `normal` (ADR-0019 §5). */
export const PRIORITY_BANDS = ['high', 'normal'] as const;
export const PriorityBandSchema = z.enum(PRIORITY_BANDS);
export type PriorityBand = z.infer<typeof PriorityBandSchema>;

export const priorityBandOf = (urgency: PriorityUrgency | null | undefined): PriorityBand =>
  urgency === 'high' ? 'high' : 'normal';

/** Event types a person can tune; everything else (invitations, jobs) is transactional. */
export const NOTIFICATION_EVENT_TYPES = [
  'ticket.created',
  'ticket.updated',
  'ticket.transitioned',
  'ticket.assigned',
  'comment.added',
  'attachment.added',
  'approval.requested',
  'approval.decided',
  'sla.breached',
] as const satisfies readonly EventType[];
export const NotificationEventTypeSchema = z.enum(NOTIFICATION_EVENT_TYPES);
export type NotificationEventType = z.infer<typeof NotificationEventTypeSchema>;

export const ChannelTogglesSchema = z.strictObject({
  inApp: z.boolean(),
  email: z.boolean(),
});
export type ChannelToggles = z.infer<typeof ChannelTogglesSchema>;

/**
 * Full matrix `{ [eventType]: { high: { inApp, email }, normal: { inApp, email } } }` as
 * returned by `GET /v1/me/notification-preferences` (defaults merged with the user's rows).
 */
export const NotificationPreferenceMatrixSchema = z.record(
  NotificationEventTypeSchema,
  z.record(PriorityBandSchema, ChannelTogglesSchema),
);
export type NotificationPreferenceMatrix = z.infer<typeof NotificationPreferenceMatrixSchema>;

/** `PUT /v1/me/notification-preferences`: a sparse patch, merged onto the current matrix. */
export const PutNotificationPreferencesSchema = z.partialRecord(
  NotificationEventTypeSchema,
  z.partialRecord(PriorityBandSchema, ChannelTogglesSchema.partial()),
);
export type PutNotificationPreferences = z.infer<typeof PutNotificationPreferencesSchema>;

/** Normal-band event types that email by default; `high` emails for every type (S-08-03). */
const NORMAL_BAND_EMAIL: ReadonlySet<NotificationEventType> = new Set<NotificationEventType>([
  'comment.added',
  'ticket.assigned',
  'approval.requested',
  'approval.decided',
]);

/** In-app on everywhere; email on for `high`, and for `normal` only where `NORMAL_BAND_EMAIL` says. */
export const DEFAULT_PREFERENCES: NotificationPreferenceMatrix =
  NotificationPreferenceMatrixSchema.parse(
    Object.fromEntries(
      NOTIFICATION_EVENT_TYPES.map((type) => [
        type,
        {
          high: { inApp: true, email: true },
          normal: { inApp: true, email: NORMAL_BAND_EMAIL.has(type) },
        },
      ]),
    ),
  );

/** Applies a sparse patch to a full matrix; unknown cells keep their value. */
export function mergePreferences(
  base: NotificationPreferenceMatrix,
  patch: PutNotificationPreferences,
): NotificationPreferenceMatrix {
  return NotificationPreferenceMatrixSchema.parse(
    Object.fromEntries(
      NOTIFICATION_EVENT_TYPES.map((type) => [
        type,
        Object.fromEntries(
          PRIORITY_BANDS.map((band) => [
            band,
            { ...base[type][band], ...(patch[type]?.[band] ?? {}) },
          ]),
        ),
      ]),
    ),
  );
}

/* ----------------------------- inbox ----------------------------- */

export const NOTIFICATION_TITLE_MAX = 200;
export const NOTIFICATION_BODY_MAX = 2000;

/**
 * `notification.payload`: rendered once by the notifications worker. `data` carries the ids
 * the UI links to (`commentId`, `authorId`, `approvalId`, …), never labels beyond the text.
 */
export const NotificationPayloadSchema = z.looseObject({
  title: z.string().min(1).max(NOTIFICATION_TITLE_MAX),
  body: z.string().max(NOTIFICATION_BODY_MAX).nullable().default(null),
  ticketKey: TicketKeySchema.nullable().default(null),
  data: z.record(z.string(), z.unknown()).default({}),
});
export type NotificationPayload = z.infer<typeof NotificationPayloadSchema>;

/** Item of `GET /v1/notifications` (S-08-01): the row with its payload lifted to the top. */
export const NotificationSchema = z.object({
  id: z.uuid(),
  /** Source event type (`comment.added`, `approval.requested`, …). */
  kind: z.string().min(1),
  ticketId: z.uuid().nullable(),
  ticketKey: TicketKeySchema.nullable(),
  title: z.string(),
  body: z.string().nullable(),
  data: z.record(z.string(), z.unknown()),
  readAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});
export type Notification = z.infer<typeof NotificationSchema>;

export const NotificationListQuerySchema = PageQuerySchema.extend({
  unreadOnly: z.stringbool().optional(),
});
export type NotificationListQuery = z.infer<typeof NotificationListQuerySchema>;

export const NotificationListResponseSchema = z.object({
  items: z.array(NotificationSchema),
  nextCursor: z.string().nullable(),
  /** Unread rows of the caller across all pages. */
  unreadCount: z.int().min(0),
});
export type NotificationListResponse = z.infer<typeof NotificationListResponseSchema>;

/** `POST /v1/notifications/read-all`. */
export const ReadAllResponseSchema = z.object({ updated: z.int().min(0) });
export type ReadAllResponse = z.infer<typeof ReadAllResponseSchema>;

/* ----------------------------- send log ----------------------------- */

export const NOTIFICATION_DELIVERY_STATUSES = ['sent', 'failed', 'suppressed'] as const;
export const NotificationDeliveryStatusSchema = z.enum(NOTIFICATION_DELIVERY_STATUSES);
export type NotificationDeliveryStatus = z.infer<typeof NotificationDeliveryStatusSchema>;

/**
 * One row per (event, recipient, channel): the exactly-once guard for emails (S-08-05) and
 * the audit of suppressed sends (preferences, guest + internal content, suppression list).
 */
export const NotificationDeliverySchema = z.object({
  id: z.uuid(),
  eventId: z.uuid(),
  recipientId: z.uuid(),
  channel: NotificationChannelSchema,
  status: NotificationDeliveryStatusSchema,
  providerMessageId: z.string().nullable(),
  error: z.string().nullable(),
  createdAt: z.iso.datetime(),
});
export type NotificationDelivery = z.infer<typeof NotificationDeliverySchema>;

/* ----------------------------- email suppression ----------------------------- */

export const EMAIL_SUPPRESSION_REASONS = ['bounce', 'complaint', 'manual'] as const;
export const EmailSuppressionReasonSchema = z.enum(EMAIL_SUPPRESSION_REASONS);
export type EmailSuppressionReason = z.infer<typeof EmailSuppressionReasonSchema>;

/** Addresses no email goes to until an admin clears them (S-08-08; handling lands in Bolt 10). */
export const EmailSuppressionSchema = z.object({
  id: z.uuid(),
  email: z.email(),
  reason: EmailSuppressionReasonSchema,
  /** The SES/SNS notification that caused it, when any. */
  source: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.iso.datetime(),
});
export type EmailSuppression = z.infer<typeof EmailSuppressionSchema>;
